import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { verify2fa } from "../services/auth.service";
import { saveAccessToken, saveRefreshToken } from "../utils/storage.utils";
import type { IVerify2faRequest } from "../types/auth.types";

interface UseVerify2faOptions {
  pendingAccessToken: string;
  onVerified?: () => void;
  onExpired?: () => void;
}

export const useVerify2fa = ({
  pendingAccessToken,
  onVerified,
  onExpired,
}: UseVerify2faOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IVerify2faRequest) => verify2fa(data, pendingAccessToken),
    onSuccess: async (data) => {
      try {
        if (data.access_token) {
          await saveAccessToken(data.access_token);
        }
        if (data.refresh_token) {
          await saveRefreshToken(data.refresh_token);
        }
      } catch {
        // localStorage unavailable — surface to user and abort so tokens are
        // never partially written and invalidateQueries is not called with a
        // missing access token.
        toast.error("Could not save your session. Please try again.");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["user"] });
      onVerified?.();
    },
    onError: (error) => {
      console.error("2FA verification failed:", error);

      const status = isAxiosError(error) ? error.response?.status : undefined;
      let errorMessage = "Invalid security code. Please try again.";

      if (status === 429) {
        errorMessage = "Too many attempts. Please wait a moment and try again.";
      } else if (status === 401) {
        errorMessage = "Verification session expired. Please sign in again.";
      } else {
        const data: unknown = isAxiosError(error) ? error.response?.data : undefined;
        if (
          typeof data === "object" &&
          data != null &&
          "detail" in data &&
          typeof data.detail === "string"
        ) {
          errorMessage = data.detail;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
      }

      toast.error(errorMessage);
      // Notify the parent AFTER the toast so sonner has queued the message
      // before any state mutation tears down the dialog tree.
      if (status === 401) onExpired?.();
    },
  });
};

export default useVerify2fa;
